import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { invoiceTemplateService } from '../../api/services';
import { InvoiceTemplate, TemplateLayout } from '../../types';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import InvoiceLayoutEditor from './InvoiceLayoutEditor';
import { safeStorage } from '../../utils/safeStorage';

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

const InvoiceTemplateForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#1d4ed8');
  const [footerText, setFooterText] = useState('');
  const [legalMentions, setLegalMentions] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [showQrCode, setShowQrCode] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [layout, setLayout] = useState<TemplateLayout | null>(null);
  const [layoutEnabled, setLayoutEnabled] = useState(false);

  // Live PDF preview (right-hand pane).
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(() => {
    return safeStorage.getItem('tunifact-template-preview') !== 'off';
  });
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewBlobUrlRef = useRef<string | null>(null);

  // Snapshot the values that actually change what the PDF looks like. Used as a
  // stable dep so the preview only re-fetches when something visual changed.
  const previewKey = useMemo(() => JSON.stringify({
    primaryColor,
    footerText,
    legalMentions,
    paymentTerms,
    showQrCode,
    layoutEnabled,
    layout: layoutEnabled ? layout : null,
  }), [primaryColor, footerText, legalMentions, paymentTerms, showQrCode, layoutEnabled, layout]);

  useEffect(() => {
    if (!isEdit) return;
    invoiceTemplateService.getById(Number(id))
      .then((t) => {
        setName(t.name);
        setPrimaryColor(t.primaryColor || '#1d4ed8');
        setFooterText(t.footerText || '');
        setLegalMentions(t.legalMentions || '');
        setPaymentTerms(t.paymentTerms || '');
        setShowQrCode(t.showQrCode !== false);
        setIsDefault(Boolean(t.isDefault));
        if (t.layout) {
          try {
            const parsed = JSON.parse(t.layout) as TemplateLayout;
            setLayout(parsed);
            setLayoutEnabled(true);
          } catch {
            setLayout(null);
          }
        }
      })
      .catch(() => { toast.error('Modèle introuvable'); navigate('/parametres/modeles'); })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  // Debounced live preview — re-renders 600ms after the last edit.
  useEffect(() => {
    if (!showPreview) return;
    if (!HEX_RE.test(primaryColor)) return;
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    previewTimeoutRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const blob = await invoiceTemplateService.previewPdf({
          name: name.trim() || 'Aperçu',
          primaryColor,
          footerText: footerText.trim() || undefined,
          legalMentions: legalMentions.trim() || undefined,
          paymentTerms: paymentTerms.trim() || undefined,
          showQrCode,
          layout: layoutEnabled && layout ? JSON.stringify(layout) : undefined,
        });
        const url = URL.createObjectURL(blob);
        if (previewBlobUrlRef.current) URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = url;
        setPreviewUrl(url);
      } catch (err: any) {
        setPreviewError(err?.response?.data?.message || "Aperçu indisponible");
      } finally {
        setPreviewLoading(false);
      }
    }, 600);
    return () => {
      if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, showPreview]);

  useEffect(() => {
    return () => {
      if (previewBlobUrlRef.current) URL.revokeObjectURL(previewBlobUrlRef.current);
    };
  }, []);

  useEffect(() => {
    safeStorage.setItem('tunifact-template-preview', showPreview ? 'on' : 'off');
  }, [showPreview]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Le nom est requis'); return; }
    if (!HEX_RE.test(primaryColor)) { toast.error('Couleur invalide (format #RRGGBB)'); return; }

    const payload: Partial<InvoiceTemplate> = {
      name: name.trim(),
      primaryColor,
      footerText: footerText.trim() || undefined,
      legalMentions: legalMentions.trim() || undefined,
      paymentTerms: paymentTerms.trim() || undefined,
      showQrCode,
      layout: layoutEnabled && layout ? JSON.stringify(layout) : undefined,
      isDefault,
    };

    try {
      setSaving(true);
      if (isEdit) {
        await invoiceTemplateService.update(Number(id), payload);
        toast.success('Modèle mis à jour');
      } else {
        await invoiceTemplateService.create(payload);
        toast.success('Modèle créé');
      }
      navigate('/parametres/modeles');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/parametres/modeles" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Retour à la liste
        </Link>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
            showPreview
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <DocumentTextIcon className="h-4 w-4" />
          {showPreview ? 'Aperçu PDF activé' : 'Aperçu PDF'}
        </button>
      </div>

      <h2 className="text-lg font-semibold">
        {isEdit ? 'Modifier le modèle' : 'Nouveau modèle de facture'}
      </h2>

      <div className={showPreview ? 'grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6' : 'max-w-5xl'}>
      <form onSubmit={onSubmit} className="card space-y-4 p-6">
        <div>
          <label className="label">Nom du modèle *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex. : Standard, Clients gouvernementaux"
            className="input-field"
            maxLength={120}
          />
        </div>

        <div>
          <label className="label">Couleur principale</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="h-10 w-16 cursor-pointer rounded border border-gray-300"
            />
            <input
              type="text"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#1d4ed8"
              className="input-field font-mono w-32"
              maxLength={7}
            />
            <span className="text-xs text-gray-500">
              Utilisée pour le titre et l'accent du PDF.
            </span>
          </div>
        </div>

        <div>
          <label className="label">Conditions de paiement</label>
          <textarea
            value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)}
            placeholder="Ex. : Conditions de règlement : 30 jours net à compter de la date de facture."
            className="input-field"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">
            Affichées au bas de chaque facture. Laissez vide pour utiliser le texte par défaut.
          </p>
        </div>

        <div>
          <label className="label">Mentions légales</label>
          <textarea
            value={legalMentions}
            onChange={(e) => setLegalMentions(e.target.value)}
            placeholder="Ex. : TVA non applicable, art. 293 B du CGI."
            className="input-field"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">
            Texte additionnel à afficher en pied de facture (clause d'exonération, RCS, etc.).
          </p>
        </div>

        <div>
          <label className="label">Pied de page</label>
          <textarea
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
            placeholder="Ex. : Adresse, téléphone, site web visibles sur toutes les pages."
            className="input-field"
            rows={2}
          />
          <p className="text-xs text-gray-500 mt-1">
            Apparaît dans le pied de chaque page du PDF, sous le RIB.
          </p>
        </div>

        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <input
              id="showQrCode"
              type="checkbox"
              checked={showQrCode}
              onChange={(e) => setShowQrCode(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            <label htmlFor="showQrCode" className="text-sm text-gray-700 font-medium">
              Afficher le QR code de paiement
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            QR code de règlement (lien de paiement ou RIB) affiché en bas des documents.
          </p>
        </div>

        <div className="pt-2 border-t border-gray-100 space-y-3">
          <div className="flex items-center gap-2">
            <input
              id="layoutEnabled"
              type="checkbox"
              checked={layoutEnabled}
              onChange={(e) => setLayoutEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            <label htmlFor="layoutEnabled" className="text-sm text-gray-700 font-medium">
              Personnaliser la mise en page (drag-and-drop)
            </label>
            <span className="text-xs text-gray-500">
              {layoutEnabled
                ? 'Layout actif — le PDF utilisera les positions ci-dessous.'
                : 'Décoché : layout par défaut (en-tête classique).'}
            </span>
          </div>

          {layoutEnabled && (
            <InvoiceLayoutEditor value={layout} onChange={setLayout} />
          )}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <input
            id="isDefault"
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600"
          />
          <label htmlFor="isDefault" className="text-sm text-gray-700">
            Définir comme modèle par défaut pour les nouvelles factures
          </label>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Enregistrement…' : (isEdit ? 'Mettre à jour' : 'Créer le modèle')}
          </button>
          <Link to="/parametres/modeles" className="btn-secondary">Annuler</Link>
        </div>
      </form>

      {showPreview && (
        <aside className="hidden xl:block">
          <div className="sticky top-4 card p-0 overflow-hidden border border-gray-200">
            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <DocumentTextIcon className="h-4 w-4" />
                Aperçu PDF
                {previewLoading && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-gray-500">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    Actualisation…
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Masquer
              </button>
            </div>
            <div className="bg-gray-100" style={{ height: 'calc(100vh - 8rem)' }}>
              {previewError && !previewUrl ? (
                <div className="h-full flex items-center justify-center p-6 text-center text-sm text-gray-500">
                  {previewError}
                </div>
              ) : previewUrl ? (
                <iframe
                  title="Aperçu PDF modèle"
                  src={previewUrl}
                  className="w-full h-full border-0"
                  data-testid="template-pdf-preview"
                />
              ) : (
                <div className="h-full flex items-center justify-center p-6 text-center text-sm text-gray-400">
                  Génération de l'aperçu…
                </div>
              )}
            </div>
          </div>
        </aside>
      )}
      </div>
    </div>
  );
};

export default InvoiceTemplateForm;
